import { defineKey as T } from 'ha/i18n';

export const FacilitiesTranslations = {
  ac: T('Air conditioning'),
  allergy_friendly: T('Allergy friendly'),
  animal_allowed: T('Animal allowed'),
  balcony_terrace: T('Balcony/Terrace'),
  basement: T('Basement'),
  bathroom: T('Bathroom'),
  bed: T('Bed'),
  bedroom_count: T('Bedroom count'),
  bedroom_furnished: T('Bedroom furnished'),
  bedroom_size: T('Bedroom size'),
  cleaning_common: T('Cleaning common room incl.'),
  cleaning_private: T('Cleaning private room incl.'),
  closet: T('Closet'),
  desk: T('Desk'),
  dishwasher: T('Dishwasher'),
  dryer: T('Dryer'),
  electricity_included: T('Electricity included'),
  flooring: T('Flooring'),
  garden: T('Garden'),
  gas_cost_included: T('Gas cost included'),
  heating: T('Heating'),
  housemates_gender: T('Housemates gender'),
  internet: T('Internet'),
  internet_included: T('Internet included'),
  kitchen: T('Kitchen'),
  kitchenware: T('Kitchenware'),
  living_room: T('Living room'),
  lock: T('Lock on bedroom'),
  lroom_furniture: T('Living room furniture'),
  parking: T('Parking'),
  play_music: T('Play musical instrument'),
  registration_possible: T('Registration possible'),
  smoking_allowed: T('Smoking allowed'),
  toilet: T('Toilet'),
  total_size: T('Total size'),
  tv: T('TV'),
  washing_machine: T('Washing machine'),
  water_cost_included: T('Water cost included'),
  wheelchair_accessible: T('Wheelchair accessible'),
  wifi: T('Wifi'),
};

export const Values = {
  Carpet: 'carpet',
  Central: 'central',
  Discussable: 'discussable',
  Electrical: 'electrical',
  Gas: 'gas',
  Laminate: 'laminate',
  LookingForJob: 'looking for a job',
  No: 'no',
  Other: 'other',
  OutsideOnly: 'outside_only',
  Plastic: 'plastic',
  PreferredFemale: 'female',
  PreferredMale: 'male',
  Private: 'private',
  Shared: 'shared',
  SharedWithBoth: 'shared_w_both',
  SharedWithBoys: 'shared_w_boys',
  SharedWithGirls: 'shared_w_girls',
  Stone: 'stone',
  Student: 'student',
  Studio: '0',
  Wood: 'wood',
  WoodStove: 'wood_stove',
  Working: 'working',
  WorkingStudent: 'working student',
  Yes: 'yes',
};

export const FacilitiesValuesTranslations = {
  [Values.Carpet]: T('Carpet'),
  [Values.Central]: T('Central heating'),
  [Values.Discussable]: T('Discussable'),
  [Values.Electrical]: T('Electrical'),
  [Values.Gas]: T('Gas'),
  [Values.Laminate]: T('Laminate'),
  [Values.LookingForJob]: 'looking for a job',
  [Values.No]: T('No'),
  [Values.Other]: T('Other'),
  [Values.OutsideOnly]: T('Outside only'),
  [Values.Plastic]: T('Plastic'),
  [Values.PreferredFemale]: T('female'),
  [Values.PreferredMale]: T('male'),
  [Values.Private]: T('Private'),
  [Values.Shared]: T('Shared'),
  [Values.SharedWithBoth]: T('Shared with boys/girls'),
  [Values.SharedWithBoys]: T('Shared with boys'),
  [Values.SharedWithGirls]: T('Shared with girls'),
  [Values.Stone]: T('Stone'),
  [Values.Student]: T('student'),
  [Values.Studio]: T('Studio'),
  [Values.Wood]: T('Wood'),
  [Values.WoodStove]: T('Wood Stove'),
  [Values.Working]: T('working'),
  [Values.WorkingStudent]: T('working student'),
  [Values.Yes]: T('Yes'),
};

export const Facilities = {
  // General
  allergy_friendly: [Values.No, Values.Yes],
  housemates_gender: [
    Values.SharedWithBoys,
    Values.SharedWithGirls,
    Values.SharedWithBoth,
  ],
  registration_possible: [Values.No, Values.Yes],
  tenant_status: [
    Values.Working,
    Values.Student,
    Values.WorkingStudent,
    Values.LookingForJob,
  ],

  // Bedroom
  bedroom_count: ['0', '1', '2', '3', '4', '5', '6', '7', '8+'],
  bedroom_furnished: [Values.No, Values.Yes],
  bedroom_size: ['integer'],
  lock: [Values.No, Values.Yes],

  // Areas
  balcony_terrace: [Values.No, Values.Shared, Values.Private],
  basement: [Values.No, Values.Shared, Values.Private],
  bathroom: [
    Values.No,
    Values.Private,
    Values.SharedWithBoys,
    Values.SharedWithGirls,
    Values.SharedWithBoth,
  ],
  garden: [Values.No, Values.Shared, Values.Private],
  kitchen: [Values.No, Values.Shared, Values.Private],
  living_room: [Values.No, Values.Shared, Values.Private],
  parking: [Values.No, Values.Shared, Values.Private],
  toilet: [Values.No, Values.Shared, Values.Private],
  total_size: ['integer'],
  wheelchair_accessible: [Values.No, Values.Yes],

  // Utilities
  ac: [Values.No, Values.Yes],
  bed: [Values.No, Values.Yes],
  closet: [Values.No, Values.Yes],
  desk: [Values.No, Values.Yes],
  dishwasher: [Values.No, Values.Yes],
  dryer: [Values.No, Values.Yes],
  flooring: [
    Values.Laminate,
    Values.Carpet,
    Values.Stone,
    Values.Wood,
    Values.Plastic,
    Values.Other,
  ],
  heating: [
    Values.No,
    Values.Central,
    Values.Gas,
    Values.WoodStove,
    Values.Electrical,
  ],
  internet: [Values.No, Values.Yes],
  kitchenware: [Values.No, Values.Shared, Values.Private],
  lroom_furniture: [Values.No, Values.Yes],
  tv: [Values.No, Values.Yes],
  washing_machine: [Values.No, Values.Yes],
  wifi: [Values.No, Values.Yes],

  // Rules
  animal_allowed: [Values.No, Values.Yes, Values.Discussable],
  cleaning_common: [Values.No, Values.Yes],
  cleaning_private: [Values.No, Values.Yes],
  play_music: [Values.No, Values.Yes, Values.Discussable],
  smoking_allowed: [
    Values.No,
    Values.Yes,
    Values.Discussable,
    Values.OutsideOnly,
  ],

  // Additional costs
  water_cost_included: [Values.No, Values.Yes],
  gas_cost_included: [Values.No, Values.Yes],
  electricity_included: [Values.No, Values.Yes],
  internet_included: [Values.No, Values.Yes],
};

export const FacilitiesKeys = Object.keys(Facilities);

export const Groups = {
  additional_costs: [
    'water_cost_included',
    'gas_cost_included',
    'electricity_included',
    'internet_included',
  ],
  areas: [
    'kitchen',
    'bathroom',
    'toilet',
    'living_room',
    'balcony_terrace',
    'garden',
    'basement',
    'parking',
    'wheelchair_accessible',
    'allergy_friendly',
  ],
  bedroom: ['bedroom_size', 'bedroom_count', 'bedroom_furnished', 'lock'],
  general: [
    'total_size',
    'housemates_gender',
    'registration_possible',
    'tenant_status',
  ],
  rules: [
    'cleaning_common',
    'cleaning_private',
    'animal_allowed',
    'play_music',
    'smoking_allowed',
  ],
  utilities: [
    'desk',
    'closet',
    'tv',
    'washing_machine',
    'dryer',
    'dishwasher',
    'kitchenware',
    'internet',
    'wifi',
    'heating',
    'ac',
    'flooring',
    'lroom_furniture',
    'bed',
  ],
};

export const GroupsOrder = [
  'general',
  'bedroom',
  'areas',
  'utilities',
  'additional_costs',
  'rules',
];

export const FacilitiesGroupsTranslations = {
  general: T('General'),
  bedroom: T('Bedroom'),
  areas: T('Areas'),
  utilities: T('Utilities'),
  additional_costs: T('Additional costs'),
  rules: T('Rules'),
};

export interface Facilities {
  ac: string | null;
  allergy_friendly: string | null;
  animal_allowed: string | null;
  balcony_terrace: string | null;
  basement: string | null;
  bathroom: string | null;
  bed: string | null;
  bedroom_count: string | null;
  bedroom_furnished: string | null;
  bedroom_size: string | null;
  cleaning_common: string | null;
  cleaning_private: string | null;
  closet: string | null;
  desk: string | null;
  dishwasher: string | null;
  dryer: string | null;
  electricity_included: string | null;
  flooring: string | null;
  garden: string | null;
  gas_cost_included: string | null;
  heating: string | null;
  housemates_gender: string | null;
  internet: string | null;
  internet_included: string | null;
  kitchen: string | null;
  kitchenware: string | null;
  living_room: string | null;
  lock: string | null;
  lroom_furniture: string | null;
  parking: string | null;
  play_music: string | null;
  registration_possible: string | null;
  smoking_allowed: string | null;
  tenant_status: string | null;
  toilet: string | null;
  total_size: string | null;
  tv: string | null;
  washing_machine: string | null;
  water_cost_included: string | null;
  wheelchair_accessible: string | null;
  wifi: string | null;
}
